<template>
  <Conference v-if="!loading" :id="id" />

  <div v-else>
    {{ loadingMessage }}
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { config, PREFERENCELIST } from '../config';
import { getApp } from '../models/App';
import { Connection } from '../models/Connection';
import Conference from './Conference.vue';
import { getJitsiId } from '../utils';

const props = defineProps<{
  id: string;
}>();

const loading = ref(true);
const route = useRoute();
const token = route.query.token as string;

// Handle the "loading message" when the page initially loads
const loadingMessage = ref('Working on it...');

// const isRecorder = route.hash.startsWith('#config.iAmRecorder=true');
const isRecorder = route.hash.includes('config.iAmRecorder=true');

// Read appData.localStorageContent from URL fragment and populate localStorage.
// This is how Jibri passes recorder XMPP credentials to the frontend
// (via Jitsi's standard config.useHostPageLocalStorage mechanism).
//
// SECURITY: Gated on recorder mode + allowlisted keys to prevent untrusted
// fragments from writing arbitrary state. This page must NEVER process the
// fragment when loaded by a real user (only by Jibri's headless Chrome).
if (isRecorder) {
  const ALLOWED_KEYS = new Set([
    'xmpp_username_override',
    'xmpp_password_override',
    'callStatsUserName',
  ]);

  const hashContent = window.location.hash.slice(1);
  const match = hashContent.match(/(?:^|&)appData\.localStorageContent=([^&]*)/);
  if (match) {
    try {
      const raw = decodeURIComponent(match[1]);
      const outer = JSON.parse(raw);
      const data = typeof outer === 'string' ? JSON.parse(outer) : outer;
      let populated = 0;
      for (const [key, value] of Object.entries(data)) {
        if (!ALLOWED_KEYS.has(key)) {
          console.warn('[recorder-check] ignoring unexpected key:', key);
          continue;
        }
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        window.localStorage.setItem(key, stringValue);
        populated++;
      }
      // Diagnostic kept during Phase 4 bring-up; remove or guard behind a debug
      // flag before production deployment.
      console.log('[recorder-check] localStorage populated, key count:', populated);
    } catch (e) {
      console.warn('[recorder-check] failed to parse appData.localStorageContent', e);
    }
  }
}


if (!token) {
  if (!isRecorder) {
    throw new Error('No Token provided');
  }
}

const app = getApp();
const connection = new Connection(props.id, token, config).save();

connection.on('connection-failed', () => {
  loadingMessage.value = 'Uh oh! Something is wrong with the link you are using to join the session. Please get a new one.';
});

app.isRecorder = isRecorder;
app.connection = connection;

watch(
  () => connection.connected,
  (newValue) => {
    if (newValue) {
      const jitsiId = getJitsiId(connection);
      app.initWs(isRecorder, isRecorder ? 'recorder' : token, jitsiId);
      loading.value = false;
    }
  },
);

watch(
  () => app.isAudioEnabled,
  (newValue) => {
    app.send(`[track]0${app.connection.id}:${newValue}`);
  },
);
watch(
  () => app.isVideoEnabled,
  (newValue) => {
    app.send(`[track]1${app.connection.id}:${newValue}`);
  },
);
watch(
  () => app.isScreenShareEnabled,
  (newValue) => {
    app.send(`[track]2${app.connection.id}:${newValue}`);
  },
);

onBeforeUnmount(() => {
  app.ws?.close();
  connection.disconnect();
});

// If the user has not set preferences yet, mark them all as checked/on by default
for (const key of Object.keys(PREFERENCELIST)) {
  if (localStorage.getItem(key) === null) {
    localStorage.setItem(key, JSON.stringify(true));
  }
}
</script>
